import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import type { CCPOfferingContent, CCPPricingPlanTier } from '@/contentful/product-catalog.tsx';
import { getCurrencyFromUserLocale } from '@/utils/useGeoCurrency.ts';

import { getPricingTiersTableData } from './pricing-tiers-table-client.ts';
import { pricingTiersTableMessages } from './pricing-tiers-table-messages.ts';

const tableCss = css({
	width: '100%',
	tableLayout: 'fixed',
	borderSpacing: token('space.025'),
	textAlign: 'center',
	color: token('color.text'),
});
const captionCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
});
const linkCss = css({
	color: token('color.link'),
	textDecoration: 'underline',
});
const headingCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
	fontWeight: token('font.weight.regular'),
});
const tierCss = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
	fontWeight: token('font.weight.regular'),
});
const priceCss = css({
	backgroundColor: token('color.background.neutral.subtle'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.100'),
});

export interface UniformPricingTiersTableProps {
	sku: string;
	title: string;
	titleHref: string;
	billingCycle: 'MONTH' | 'YEAR';
	unit?: 'user' | 'creator' | 'agent';
	countryCode?: string;
}

interface FetchedPricingData {
	sku: string;
	data?: Pick<CCPOfferingContent, 'defaultPricingPlans'>;
}

const UniformPricingTiersTable = (props: UniformPricingTiersTableProps) => {
	const { sku, title, titleHref, billingCycle, unit = 'user', countryCode = 'US' } = props;
	const intl = useIntl();
	const [fetchedPricingData, setFetchedPricingData] = useState<FetchedPricingData>();

	useEffect(() => {
		if (!sku) {
			return;
		}

		let isCurrent = true;
		void getPricingTiersTableData({ sku })
			.then((data) => {
				if (isCurrent) {
					setFetchedPricingData({ sku, data });
				}
			})
			.catch(() => {
				if (isCurrent) {
					setFetchedPricingData({ sku });
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [sku]);

	const currency = getCurrencyFromUserLocale(countryCode);
	const offering = fetchedPricingData?.sku === sku ? fetchedPricingData.data : undefined;
	const plan = offering?.defaultPricingPlans?.find(
		(item) =>
			item.primaryCycle_interval === billingCycle &&
			item.currency === currency &&
			item.type === 'COMMERCIAL' &&
			item.status === 'ACTIVE' &&
			item.activatedWithReason === 'DEFAULT_PRICING',
	);
	const tiers = plan?.items?.[0]?.tiers;
	const tierHeadingByUnit = {
		user: pricingTiersTableMessages.userTierHeading,
		creator: pricingTiersTableMessages.creatorTierHeading,
		agent: pricingTiersTableMessages.agentTierHeading,
	};
	const monthlyPriceByUnit = {
		user: pricingTiersTableMessages.monthlyUserPrice,
		creator: pricingTiersTableMessages.monthlyCreatorPrice,
		agent: pricingTiersTableMessages.monthlyAgentPrice,
	};
	const tierHeading = intl.formatMessage(tierHeadingByUnit[unit]);
	const priceHeading = intl.formatMessage(
		billingCycle === 'MONTH'
			? pricingTiersTableMessages.monthlyPriceHeading
			: pricingTiersTableMessages.annualPriceHeading,
	);

	if (!tiers?.length) {
		return null;
	}

	return (
		<table css={tableCss} data-offering-sku={sku}>
			<caption css={captionCss}>
				<a css={linkCss} href={titleHref}>
					{title}
				</a>
			</caption>
			<thead>
				<tr>
					<th css={headingCss} scope="col">
						{tierHeading}
					</th>
					<th css={headingCss} scope="col">
						{priceHeading}
					</th>
				</tr>
			</thead>
			<tbody>
				{tiers.map((tier) => (
					<tr key={`${tier.floor}-${tier.ceiling}`}>
						<th css={tierCss} scope="row">
							{formatTierRange(tier)}
						</th>
						<td css={priceCss}>
							{billingCycle === 'MONTH'
								? intl.formatMessage(monthlyPriceByUnit[unit], {
										price: formatTierPrice(tier, currency),
									})
								: formatTierPrice(tier, currency)}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
};

export default UniformPricingTiersTable;

function formatTierRange(tier: CCPPricingPlanTier): string {
	const floor = Math.max(1, tier.floor);
	const start = new Intl.NumberFormat('en-US').format(floor);
	if (!tier.ceiling || tier.ceiling < floor) {
		return `${start}+`;
	}
	return `${start} - ${new Intl.NumberFormat('en-US').format(tier.ceiling)}`;
}

function formatTierPrice(tier: CCPPricingPlanTier, currency: 'USD' | 'JPY'): string {
	const amount = tier.flatAmount || tier.unitAmount;
	const value = currency === 'USD' ? amount / 100 : amount;
	const locale = currency === 'JPY' ? 'ja-JP' : 'en-US';
	return `${currency} ${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}`;
}
